/**
 * PUTs a composited profile photo directly to the write SAS URL minted by
 * `createProfilePhotoUploadUrl` — there is no separate confirmation call, so a successful PUT
 * here *is* the save.
 */

/**
 * A failed PUT to the SAS URL. Never carries the URL itself in `message` — it's a live
 * credential (the SAS query string grants write access), and this error's message is safe to log
 * or hand to `trackException`.
 */
export class ProfilePhotoUploadError extends Error {
  constructor(public readonly status: number | null) {
    super(status ? `Profile photo upload failed with status ${status}` : 'Profile photo upload failed');
    this.name = 'ProfilePhotoUploadError';
  }
}

/**
 * `Content-Type` is set explicitly rather than left to the browser's default — a SAS URL has no
 * knowledge of the body's content, so an unset header would either omit it or guess it from the
 * `Blob`'s own (possibly absent) `type`, and the legacy Drupal upload flow's silent failure this
 * ticket fixes started with exactly this kind of unchecked assumption.
 *
 * `x-ms-blob-type: BlockBlob` is required by Azure's Put Blob REST operation itself, not
 * something the SAS or the SDK adds for you — a plain `fetch` PUT without it is rejected with a
 * 400 before the bytes are ever considered (confirmed against the local Azurite emulator, which
 * implements this the same way the real service does).
 */
export async function putProfilePhoto(url: string, photo: Blob): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/png', 'x-ms-blob-type': 'BlockBlob' },
      body: photo,
    });
  } catch {
    throw new ProfilePhotoUploadError(null);
  }

  if (!response.ok) {
    throw new ProfilePhotoUploadError(response.status);
  }
}
